import { Database, Monitor, Server } from 'lucide-react';
import { useState } from 'react';

import { SelectField } from '@/shared/ui/select';

const components = [
  {
    name: 'Browser',
    location: 'User’s device',
    detail:
      'Runs the frontend. A form collects input; JavaScript sends a request and renders the response.',
    input: 'A user action or an API response',
    output: 'An HTTP request or an updated page',
    icon: '01',
  },
  {
    name: 'API / backend',
    location: 'Application server',
    detail:
      'Matches the route, validates input and coordinates storage. The API is the interface; the backend implements it.',
    input: 'Method, path and optional JSON body',
    output: 'A status and response representation',
    icon: '02',
  },
  {
    name: 'PostgreSQL',
    location: 'Database server process',
    detail:
      'Enforces table constraints and manages committed records. The browser has no direct database connection here.',
    input: 'A query from the backend',
    output: 'Rows, affected-row information or an error',
    icon: '03',
  },
] as const;
const flows = {
  read: [
    {
      node: 0,
      label: 'Request the list',
      payload: 'GET /api/employees',
      why: 'The frontend requests current data. The page shell and its data are separate.',
    },
    {
      node: 1,
      label: 'Handle the request',
      payload: 'Route → list employees',
      why: 'The backend selects the operation and asks the database for records.',
    },
    {
      node: 2,
      label: 'Read committed rows',
      payload: 'SELECT id, name, active …',
      why: 'The query reads data; it does not request a change to stored employees.',
    },
    {
      node: 1,
      label: 'Construct the response',
      payload: '200 · [{"id":7,"name":"Asha","active":true}]',
      why: 'The backend converts returned rows into the API’s JSON representation.',
    },
    {
      node: 0,
      label: 'Render the result',
      payload: 'Asha · Active',
      why: 'The page displays a representation of the response, not a direct view into database memory.',
    },
  ],
  write: [
    {
      node: 0,
      label: 'Submit the form',
      payload: 'POST /api/employees\n{"name":"  Ravi  ","active":true}',
      why: 'The browser collects the entered values. It should preserve them until success.',
    },
    {
      node: 1,
      label: 'Validate and normalize',
      payload: '"  Ravi  " → "Ravi"',
      why: 'The backend enforces the rule even if a caller bypasses the form.',
    },
    {
      node: 2,
      label: 'Insert and commit',
      payload: 'New stored row: id 9 · Ravi · true',
      why: 'The database assigns identity and commits the accepted record.',
    },
    {
      node: 1,
      label: 'Return the created record',
      payload: '201 · {"id":9,"name":"Ravi","active":true}',
      why: 'The client uses the ID and normalized values actually returned by the server.',
    },
    {
      node: 0,
      label: 'Confirm success',
      payload: 'Display Ravi · clear the form',
      why: 'Success is now observed. A rejection would preserve input and show an error instead.',
    },
  ],
} as const;
export function ApplicationMap() {
  const [mode, setMode] = useState<'read' | 'write'>('read');
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(0);
  const flow = flows[mode];
  const current = flow[step] ?? flow[0];
  const component = components[selected] ?? components[0];
  function move(next: number) {
    setStep(next);
    setSelected(flow[next]?.node ?? 0);
  }
  return (
    <>
      <div className="lab-actions">
        {(['read', 'write'] as const).map((value) => (
          <button
            key={value}
            aria-pressed={mode === value}
            onClick={() => {
              setMode(value);
              setStep(0);
              setSelected(0);
            }}
          >
            {value === 'read' ? 'Read employees' : 'Add an employee'}
          </button>
        ))}
      </div>
      <div
        className="lab-system-map"
        aria-label="Browser communicates with API; API communicates with PostgreSQL"
      >
        {components.map((item, index) => (
          <div className="lab-system-slot" key={item.name}>
            <button
              className="lab-system-node"
              aria-pressed={selected === index}
              data-active={current.node === index}
              onClick={() => setSelected(index)}
            >
              <span className="lab-node-icon" aria-hidden="true">
                {index === 0 ? (
                  <Monitor size={28} />
                ) : index === 1 ? (
                  <Server size={28} />
                ) : (
                  <Database size={28} />
                )}
              </span>
              <strong>{item.name}</strong>
              <small>{item.location}</small>
            </button>
            {index < 2 ? (
              <span className="lab-connector" aria-hidden="true">
                ⇄<small>{index === 0 ? 'HTTP' : 'SQL'}</small>
              </span>
            ) : null}
          </div>
        ))}
      </div>
      <div className="lab-flow-progress" aria-label="Request journey">
        {flow.map((item, index) => (
          <button
            key={item.label}
            aria-current={step === index ? 'step' : undefined}
            onClick={() => move(index)}
          >
            <span>{index + 1}</span>
            {item.label}
          </button>
        ))}
      </div>
      <div className="lab-two">
        <div className="lab-packet">
          <small>
            STEP {step + 1} · {current.label}
          </small>
          <pre>{current.payload}</pre>
          <p>{current.why}</p>
        </div>
        <div className="lab-component-detail">
          <h4>{component.name} · responsibility</h4>
          <p>{component.detail}</p>
          <dl>
            <dt>Receives</dt>
            <dd>{component.input}</dd>
            <dt>Produces</dt>
            <dd>{component.output}</dd>
          </dl>
        </div>
      </div>
      <div className="lab-actions">
        <button disabled={step === 0} onClick={() => move(step - 1)}>
          Previous step
        </button>
        <button disabled={step === flow.length - 1} onClick={() => move(step + 1)}>
          Next step
        </button>
      </div>
      <p role="status">
        Step {step + 1} of {flow.length}: {current.label}. These are logical responsibilities;
        server processes may share a machine.
      </p>
    </>
  );
}

function programTrace(flags: boolean[]) {
  const states: {
    line: number;
    count: number | null;
    flag: boolean | null;
    result: number | null;
    output: string;
    note: string;
  }[] = [];
  let count = 0;
  const add = (
    line: number,
    flag: boolean | null,
    note: string,
    result: number | null = null,
    output = '',
  ) => states.push({ line, count, flag, note, result, output });
  add(0, null, 'The function is defined. Its body has not run.');
  add(7, null, 'The call supplies flags and enters the function.');
  add(1, null, 'This call creates a local count starting at zero.');
  for (const flag of flags) {
    add(2, flag, 'The loop assigns the next item to flag.');
    add(3, flag, flag ? 'True enters the indented branch.' : 'False skips the increment.');
    if (flag) {
      count += 1;
      add(4, flag, 'Assignment replaces count with count + 1.');
    }
  }
  add(5, flags.at(-1) ?? null, 'The return value goes back to the caller.', count);
  add(7, null, 'The caller assigns the returned value to result.', count);
  add(
    8,
    null,
    'print displays result. Returning and displaying are different actions.',
    count,
    String(count),
  );
  return states;
}
export function PythonProgram() {
  const [input, setInput] = useState('mixed');
  const [step, setStep] = useState(0);
  const flags = input === 'mixed' ? [true, false, true] : input === 'false' ? [false, false] : [];
  const literal = `[${flags.map((flag) => (flag ? 'True' : 'False')).join(', ')}]`;
  const lines = [
    'def count_active(flags):',
    '    count = 0',
    '    for flag in flags:',
    '        if flag:',
    '            count = count + 1',
    '    return count',
    '',
    `result = count_active(${literal})`,
    'print(result)',
  ];
  const trace = programTrace(flags),
    current = trace[step];
  if (!current) return null;
  const callReturned = step >= trace.length - 3;
  return (
    <>
      <label className="lab-field">
        <span>Program input</span>
        <SelectField
          aria-label="Program input"
          value={input}
          onValueChange={(value) => {
            setInput(value);
            setStep(0);
          }}
          options={[
            { value: 'mixed', label: '[True, False, True]' },
            { value: 'false', label: '[False, False]' },
            { value: 'empty', label: '[]' },
          ]}
        />
      </label>
      <div className="lab-code-machine">
        <div>
          <h4>Source · example.py</h4>
          <pre className="lab-trace">
            {lines.map((line, index) => (
              <code key={index} aria-current={current.line === index ? 'step' : undefined}>
                <span>{index + 1}</span>
                {line || ' '}
              </code>
            ))}
          </pre>
        </div>
        <div className="lab-memory">
          <h4>Values after this step</h4>
          <dl>
            <dt>flags</dt>
            <dd>{step === 0 ? 'not assigned' : callReturned ? 'outside call' : literal}</dd>
            <dt>flag</dt>
            <dd>
              {callReturned
                ? 'outside call'
                : current.flag === null
                  ? 'not assigned'
                  : current.flag
                    ? 'True'
                    : 'False'}
            </dd>
            <dt>local count</dt>
            <dd>{step < 2 ? 'not assigned' : callReturned ? 'outside call' : current.count}</dd>
            <dt>returned value</dt>
            <dd>{current.result ?? 'not returned'}</dd>
            <dt>result</dt>
            <dd>
              {step === 0
                ? 'not assigned'
                : step >= trace.length - 2
                  ? current.result
                  : 'caller waiting'}
            </dd>
          </dl>
          <h4>Printed output</h4>
          <pre>{current.output || '(nothing printed yet)'}</pre>
        </div>
      </div>
      <div className="lab-actions">
        <button disabled={step === 0} onClick={() => setStep(step - 1)}>
          Previous line
        </button>
        <button disabled={step === trace.length - 1} onClick={() => setStep(step + 1)}>
          Execute next line
        </button>
        <button onClick={() => setStep(0)}>Reset trace</button>
      </div>
      <div role="status" className="lab-observation">
        Step {step + 1}/{trace.length} · {current.note}
      </div>
    </>
  );
}

const testCases = [
  {
    id: 'ordinary',
    label: 'Ordinary name',
    input: '"Asha"',
    expected: '"Asha"',
    why: 'An ordinary short name does not expose trimming or length defects.',
  },
  {
    id: 'spaces',
    label: 'Surrounding spaces',
    input: '" Asha "',
    expected: '"Asha"',
    why: 'Checks that normalization returns the trimmed value.',
  },
  {
    id: 'blank',
    label: 'Whitespace only',
    input: '"   "',
    expected: 'ValueError',
    why: 'Tests length after trimming, rather than before it.',
  },
  {
    id: 'exact',
    label: 'Exactly 80 characters',
    input: '"a" * 80',
    expected: '80-character string',
    why: 'Distinguishes an inclusive limit from an accidental exclusive limit.',
  },
  {
    id: 'long',
    label: '81 characters',
    input: '"a" * 81',
    expected: 'ValueError',
    why: 'Detects accepting a value beyond the maximum.',
  },
  {
    id: 'type',
    label: 'Integer input',
    input: '42',
    expected: 'TypeError',
    why: 'Detects silently converting an invalid type to text.',
  },
];
const implementations = [
  {
    id: 'correct',
    label: 'Published contract',
    code: 'check type → trim → require 1–80 → return',
    failures: [] as string[],
  },
  {
    id: 'trim',
    label: 'Returns untrimmed input',
    code: 'check type → check trimmed length → return original',
    failures: ['spaces'],
  },
  {
    id: 'boundary',
    label: 'Uses length < 80',
    code: 'check type → trim → require 1–79 → return',
    failures: ['exact'],
  },
  {
    id: 'coerce',
    label: 'Converts everything to text',
    code: 'str(input) → trim → require 1–80 → return',
    failures: ['type'],
  },
];
export function TestBench() {
  const [selected, setSelected] = useState<string[]>(['ordinary']);
  const [ran, setRan] = useState(false);
  const [detail, setDetail] = useState('ordinary');
  const active = testCases.find((test) => test.id === detail);
  if (!active) return null;
  return (
    <>
      <p>
        Rule: accept text, trim it, require 1–80 characters. Select a suite before revealing which
        implementations it catches.
      </p>
      <div className="lab-test-cases">
        {testCases.map((test) => (
          <label key={test.id}>
            <input
              type="checkbox"
              checked={selected.includes(test.id)}
              onChange={() => {
                setSelected(
                  selected.includes(test.id)
                    ? selected.filter((id) => id !== test.id)
                    : [...selected, test.id],
                );
                setRan(false);
                setDetail(test.id);
              }}
            />
            <span>
              <strong>{test.label}</strong>
              <code>{test.input}</code>
            </span>
          </label>
        ))}
      </div>
      <div className="lab-actions">
        <button disabled={!selected.length} onClick={() => setRan(true)}>
          Run selected cases
        </button>
        <button
          onClick={() => {
            setSelected(['ordinary']);
            setRan(false);
          }}
        >
          Reset suite
        </button>
      </div>
      <div className="lab-test-matrix">
        {implementations.map((implementation) => {
          const caught = implementation.failures.filter((id) => selected.includes(id));
          return (
            <div
              key={implementation.id}
              data-result={ran ? (caught.length ? 'caught' : 'pass') : 'pending'}
            >
              <strong>{implementation.label}</strong>
              <code>{implementation.code}</code>
              <span>
                {!ran
                  ? 'Predict the outcome'
                  : caught.length
                    ? `FAIL · ${caught.map((id) => testCases.find((test) => test.id === id)?.label ?? id).join(', ')}`
                    : 'PASS · selected cases only'}
              </span>
            </div>
          );
        })}
      </div>
      <div className="lab-observation" role="status">
        {ran
          ? `${implementations.slice(1).filter((implementation) => implementation.failures.some((id) => selected.includes(id))).length} of 3 defective implementations detected. Passing the selected cases does not prove every requirement.`
          : 'Choose cases that distinguish an implementation from the contract.'}
      </div>
      <p>
        <strong>{active.label}:</strong> expected {active.expected}. {active.why}
      </p>
    </>
  );
}

export function Persistence() {
  const [memory, setMemory] = useState(['Asha', 'Leo']);
  const [disk, setDisk] = useState(['Asha', 'Leo']);
  const [running, setRunning] = useState(true);
  const [message, setMessage] = useState(
    'Both stores begin with the same two records. Predict what survives an API restart.',
  );
  const [sequence, setSequence] = useState(1);
  return (
    <>
      <div className="lab-storage-comparison">
        <div className="lab-storage-bank">
          <span className="lab-bank-label">API PROCESS MEMORY</span>
          <h4>In-memory store</h4>
          <div className="lab-stored-rows">
            {memory.map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
          <small>Re-created from the sample fixture when the API restarts.</small>
        </div>
        <div className="lab-storage-bank" data-unavailable={!running}>
          <span className="lab-bank-label">POSTGRESQL · RETAINED DATA</span>
          <h4>Committed database rows</h4>
          <div className="lab-stored-rows">
            {disk.map((name) => (
              <span key={name}>{name}</span>
            ))}
          </div>
          <small>{running ? 'Database reachable' : 'Database stopped · rows remain on disk'}</small>
        </div>
      </div>
      <div className="lab-actions">
        <button
          disabled={!running}
          onClick={() => {
            const name = `Employee ${sequence}`;
            setSequence(sequence + 1);
            setMemory([...memory, name]);
            setDisk([...disk, name]);
            setMessage(
              `${name} added to memory and committed in PostgreSQL. Predict the next restart.`,
            );
          }}
        >
          Add employee to both stores
        </button>
        <button
          onClick={() => {
            setMemory(['Asha', 'Leo']);
            setMessage(
              'API restarted: its memory was re-created from fixtures. Committed PostgreSQL rows were unchanged.',
            );
          }}
        >
          Restart only the API
        </button>
        <button
          onClick={() => {
            setRunning(!running);
            setMessage(
              running
                ? 'PostgreSQL stopped. The database-backed API cannot read these retained rows: unavailability is not deletion.'
                : 'PostgreSQL restarted using the same data. Retained rows are readable again.',
            );
          }}
        >
          {running ? 'Stop PostgreSQL' : 'Restore PostgreSQL'}
        </button>
        <button
          onClick={() => {
            setMemory(['Asha', 'Leo']);
            setDisk(['Asha', 'Leo']);
            setRunning(true);
            setSequence(1);
            setMessage('Teaching model reset.');
          }}
        >
          Reset comparison
        </button>
      </div>
      <div role="status" className="lab-observation">
        {message}
      </div>
      <p>
        This demonstrates API-restart persistence with retained database files. It does not simulate
        a lost disk, backup recovery or replacement of a workshop VM.
      </p>
    </>
  );
}

const contractCases = [
  {
    action: 'List employees',
    method: 'GET',
    path: '/api/employees',
    status: '200',
    body: 'An array; [] is a successful empty result.',
    why: 'Read the collection without requesting a state change.',
  },
  {
    action: 'Create an employee',
    method: 'POST',
    path: '/api/employees',
    status: '201',
    body: 'The created record with its server-generated ID.',
    why: 'Submit a representation to the collection to create a new resource.',
  },
  {
    action: 'Read employee 7',
    method: 'GET',
    path: '/api/employees/7',
    status: '200',
    body: 'One employee, or 404 if the ID does not exist.',
    why: 'A stable identifier selects one resource, not a position in a list.',
  },
  {
    action: 'Change employee 7’s active flag',
    method: 'PATCH',
    path: '/api/employees/7',
    status: '200',
    body: 'The updated record; omitted fields stay unchanged.',
    why: 'The published PATCH contract changes only supplied fields.',
  },
  {
    action: 'Delete employee 7',
    method: 'DELETE',
    path: '/api/employees/7',
    status: '204',
    body: 'No response body. A later delete can return 404.',
    why: 'The requested state is absence of that employee. Repeating the deletion does not create another effect.',
  },
];
export function RestContract() {
  const [index, setIndex] = useState(0),
    [method, setMethod] = useState(''),
    [path, setPath] = useState(''),
    [checked, setChecked] = useState(false);
  const item = contractCases[index];
  if (!item) return null;
  return (
    <>
      <label className="lab-field">
        <span>Required action</span>
        <SelectField
          aria-label="Required action"
          value={index}
          onValueChange={(value) => {
            setIndex(Number(value));
            setMethod('');
            setPath('');
            setChecked(false);
          }}
          options={contractCases.map((entry, i) => ({ value: i, label: entry.action }))}
        />
      </label>
      <div className="lab-contract-card">
        <span>DESIGN THE REQUEST</span>
        <div className="lab-actions">
          {['GET', 'POST', 'PATCH', 'DELETE'].map((value) => (
            <button
              key={value}
              aria-pressed={method === value}
              onClick={() => {
                setMethod(value);
                setChecked(false);
              }}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="lab-actions">
          {['/api/employees', '/api/employees/7'].map((value) => (
            <button
              key={value}
              aria-pressed={path === value}
              onClick={() => {
                setPath(value);
                setChecked(false);
              }}
            >
              {value}
            </button>
          ))}
        </div>
        <pre>
          {method || '(method)'} {path || '(resource path)'}
        </pre>
        <button disabled={!method || !path} onClick={() => setChecked(true)}>
          Check API contract
        </button>
      </div>
      {checked ? (
        <div className="lab-observation" role="status">
          <strong>
            {method === item.method && path === item.path
              ? 'This matches the published contract.'
              : `Use ${item.method} ${item.path} for this contract.`}
          </strong>
          <p>{item.why}</p>
          <p>
            Success: {item.status} · {item.body}
          </p>
        </div>
      ) : null}
    </>
  );
}
